import React from 'react';
import { motion } from 'framer-motion';
import { Truck, Star, Clock, Award } from 'lucide-react';
import { getRegionMeta } from '../utils/region';
import { HowItWorks } from './HowItWorks';
import { FAQ } from './FAQ';
const stats = [{
  label: 'Years Serving WA',
  value: '10+',
  icon: Clock
}, {
  label: 'Deliveries Made',
  value: '5,000+',
  icon: Truck
}, {
  label: 'Locally Owned',
  value: '100%',
  icon: Award
}, {
  label: 'Customer Rating',
  value: '5-Star',
  icon: Star
}];
export function About() {
  // Brand + area copy follow the active domain (253 / 206 / 425) so the
  // page reads as the regional identity the visitor arrived on.
  const region = getRegionMeta();
  const brand = `${region.code}Gravel`;
  return <div className="bg-[#f1ede6] dark:bg-[#292827] transition-colors duration-300 min-h-screen" data-id="element-100" data-mp="3hi4ywdg8feot">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#e8ede4] dark:bg-[#292827] overflow-hidden" style={{
      minHeight: '700px'
    }} data-id="element-101" data-mp="3mfwmiv9ugen7">
        {/* Background Image */}
        <div className="absolute inset-0" data-id="element-102" data-mp="2lp4geo3p2mhv">
          <img src="https://cdn.magicpatterns.com/uploads/pFhc3AjvRgqipTxvhW5pi7/file_000000003d4c720cb3b00791865cbb87.png" alt="Dump trucks at gravel yard" className="w-full h-full object-cover" data-id="element-103" data-mp="2grmhlqje5nnl" />
          <div className="absolute inset-0 bg-black/30" data-id="element-104" data-mp="27rzcourgdzps" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-105" data-mp="27tsk4c9w4nyu">
          <div className="text-center max-w-3xl mx-auto" data-id="element-106" data-mp="34jt8wba9f4g5">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block py-1 px-3 rounded-xl bg-[#FDAC53]/15 text-[#b9701a] dark:text-[#FDAC53] text-sm font-bold tracking-wider uppercase mb-6" data-id="element-107" data-mp="30h6cdx2n8vcm">
              Our Story
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight" data-id="element-108" data-mp="3o7iug0rls8gx">
              Building the foundation of our community,{' '}
              <span className="text-brand-orange" data-id="element-109" data-mp="3ti4nviy3ewhy">one load at a time.</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg md:text-xl text-stone-200 dark:text-stone-300 leading-relaxed" data-id="element-110" data-mp="1dsp76h6b7vp">
              We are {brand} — your trusted local partner for premium
              landscaping materials and dependable delivery across the{' '}
              {region.name}.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative z-20 -mt-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-111" data-mp="1cu0l4p23nkye">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-112" data-mp="2xcb6mob2hn39">
          {stats.map((stat, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 30,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          y: 0,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.1,
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} whileHover={{
          y: -8,
          transition: {
            type: 'spring',
            stiffness: 400,
            damping: 20
          }
        }} className="relative bg-white dark:bg-[#474747] rounded-2xl p-6 md:p-8 text-center cursor-default group border border-stone-200 dark:border-[#555555] hover:border-brand-orange/40 dark:hover:border-brand-orange/40 transition-all duration-300 overflow-hidden shadow-md hover:shadow-xl dark:shadow-lg dark:hover:shadow-[0_8px_30px_rgba(0,0,0,0.3)]" data-id="element-113" data-mp="2tapythde3yzx" data-mpi={idx}>
              {/* Large watermark number */}
              <span className="absolute -top-4 -right-2 font-heading text-[80px] font-black text-stone-900/[0.03] dark:text-white/[0.03] group-hover:text-brand-orange/[0.06] transition-colors duration-500 pointer-events-none select-none leading-none" data-id="element-114" data-mp="1t32qn65z0zy9">
                {stat.value}
              </span>

              <div className="relative z-10" data-id="element-115" data-mp="272kglov06j97">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-orange/10 text-brand-orange mb-5 transition-all duration-300 group-hover:bg-brand-orange/20 group-hover:shadow-[0_0_24px_rgba(192,178,131,0.25)] group-hover:scale-110" data-id="element-116" data-mp="27081djgmqmzu">
                  <stat.icon className="w-7 h-7 transition-transform duration-300 group-hover:scale-110" data-id="element-117" data-mp="1jby9q0wqj50s" />
                </div>
                <div className="text-3xl md:text-4xl font-extrabold text-stone-900 dark:text-white mb-2 font-heading transition-colors duration-300 group-hover:text-brand-orange tracking-tight" data-id="element-118" data-mp="1o5jbm41g8j59">
                  {stat.value}
                </div>
                <div className="text-xs font-bold text-stone-700 dark:text-stone-200 uppercase tracking-[0.15em] transition-colors duration-300 group-hover:text-stone-700 dark:group-hover:text-stone-200" data-id="element-119" data-mp="o3wb0u243wq3">
                  {stat.label}
                </div>
              </div>

              {/* Bottom accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-brand-orange/0 group-hover:bg-brand-orange/60 transition-all duration-300" data-id="element-120" data-mp="95kd6k306hxc" />
            </motion.div>)}
        </div>
      </section>

      {/* Story Section */}
      <section id="our-story" className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 scroll-mt-[140px]" data-id="element-121" data-mp="bz0d6aefaqk">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center" data-id="element-122" data-mp="3mla17858q2yp">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="lg:col-span-5" data-id="element-123" data-mp="3mjff3qi8g4t1">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-stone-900 dark:text-white leading-tight mb-6" data-id="element-124" data-mp="32zq9xev1mqw2">
              More than just gravel. We deliver reliability.
            </h2>
            <div className="w-20 h-1.5 bg-[#FDAC53] rounded-full mb-8" data-id="element-125" data-mp="2tz1vwk5ngqj6"></div>
            <p className="text-xl text-stone-600 dark:text-stone-300 font-medium leading-relaxed font-sub" data-id="element-126" data-mp="2u14tpqd1nnyg">
              Founded with a simple mission — to make ordering and receiving
              landscaping materials as easy as possible.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="lg:col-span-7 space-y-6 text-lg text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-127" data-mp="3qo9gnpyg2vm1">
            <p data-id="element-128" data-mp="9fcs5ic8lzhq">
              <strong className="text-stone-900 dark:text-white font-semibold" data-id="element-129" data-mp="9eum5tzrw6wm">
                {brand}
              </strong>{' '}
              is a locally owned gravel delivery company serving Pierce and King
              counties — from Tacoma to Seattle and beyond. We specialize in
              bulk delivery of{' '}
              <strong className="text-stone-900 dark:text-white font-semibold" data-id="element-130" data-mp="xc9b1cimbemg">
                crushed rock, drain rock, pea gravel, road base, beauty bark,
                topsoil, and recycled aggregates
              </strong>{' '}
              — everything you need for driveways, landscaping, drainage, and
              construction projects.
            </p>
            <p data-id="element-131" data-mp="ntbcnlsnovm">
              We've built our reputation on fast delivery, fair pricing, and
              outstanding customer service. From a single 5-yard load of crushed
              gravel for your driveway to 20+ yards of topsoil for a commercial
              landscaping job, no order is too small or too large.
            </p>
            <p data-id="element-132" data-mp="1l23p1xeob98a">
              We deliver throughout{' '}
              <strong className="text-stone-900 dark:text-white font-semibold" data-id="element-133" data-mp="1l1lk8cdy3mor">
                Tacoma, Puyallup, Bonney Lake, Lakewood, Federal Way, Auburn,
                Kent, Sumner, Edgewood, Milton, Renton, Seattle, Bellevue,
                Kirkland, Redmond, and surrounding Pierce & King County
                communities
              </strong>
              . Our experienced dump truck operators know the area and will
              place your material exactly where you need it.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Works — moved here from the home page so the About page
            explains the ordering process in context. */}
      <HowItWorks data-id="element-134" data-mp="b4de4uabzhv3" />

      {/* FAQ — lives on the About page (replaces the old Project Gallery).
            Also still rendered on the home page. */}
      <FAQ data-id="element-135" data-mp="2cvgodigb6uf4" />
    </div>;
}